import type {
  ControlSettingStateType,
  PayloadControlChange,
} from '~/presentation/reports/common/type';

import {
  EControlSettingType,
  EReportControlId,
  ERFilterKey,
} from '~/presentation/reports/common/type';
import { REPORT_DATA_PRESET, REPORT_UI_PRESET } from '~/modules/report-analytic/presets';
import { checkIsChangeFilter, checkHasControlFilters } from '../../helpers';
import { REPORT_FILTER_WIDTH, usePreset } from '~/modules/report-analytic';
import { EReportPresetKey } from '~/modules/report-analytic/types';
import { ControlSettings } from '~/presentation/reports/common';
import { useDateTimePickerContext } from '~/presentation/ui';
import { BlockStack, Box, Card } from '@shopify/polaris';
import { FaqDataNumbersTrigger } from '~/modules/faq';
import { useOrderControlStore } from '../../stores';
import { useStickyElementHeight } from '~/hooks';
import { useRef } from 'react';
interface ControlFilterPanelProps {
  state?: ControlSettingStateType;
}

const metricKey = EReportPresetKey.ORDER;
const initPreset = {
  presetData: REPORT_DATA_PRESET[metricKey],
  presetUI: REPORT_UI_PRESET[metricKey],
};

export const ControlFilterPanel = ({ state }: ControlFilterPanelProps) => {
  const onControlChange = useOrderControlStore((state) => state.onControlChange);
  const setOrderControlData = useOrderControlStore((state) => state.setOrderControlData);
  const setIsChangingFilter = useOrderControlStore((state) => state.setIsChangingFilter);
  const orderControlData = useOrderControlStore((state) => state.orderControlData);
  const openOrderControl = useOrderControlStore((state) => state.openOrderControl);

  const { dateTimePicked, resetDateVersion } = useDateTimePickerContext();

  const { preset } = usePreset({ setControlData: setOrderControlData, metricKey, initPreset });

  const elementRef = useRef<HTMLDivElement>(null);
  const parentRef = useRef<HTMLDivElement>(null);
  const maxHeight = useStickyElementHeight({
    elementRef,
    parentRef,
    offset: 80,
  });

  if (!preset) return null;

  const width = !openOrderControl ? 0 : REPORT_FILTER_WIDTH;
  const opacity = openOrderControl ? 1 : 0;

  return (
    <div ref={parentRef} style={{ width, position: 'relative', opacity }}>
      <Box position="sticky" insetBlockStart="300">
        <BlockStack gap="400">
          <Card padding="0">
            <div ref={elementRef}>
              <ControlSettings
                data={orderControlData || preset.presetData}
                defaultData={preset.presetData}
                presetUI={preset.presetUI}
                state={state}
                type={EControlSettingType.ORDER}
                controlChange={(data) => {
                  onControlChange(data);
                  handleFilterChange(data);
                }}
                maxHeight={maxHeight}
                onDeleteControlUI={onDeleteControlUI}
              />
            </div>
          </Card>

          <FaqDataNumbersTrigger />
        </BlockStack>
      </Box>
    </div>
  );

  function onDeleteControlUI(id: string) {
    switch (id) {
      case ERFilterKey.EXPERIMENT:
        const isFilteredByVersion = dateTimePicked.isVersion;
        isFilteredByVersion && resetDateVersion();
        break;
      default:
        break;
    }
  }

  function handleFilterChange(data: PayloadControlChange) {
    const newFilterValue = data[EReportControlId.FILTER];
    const isAddFilters = !!newFilterValue;
    const existingFilterValue = orderControlData?.[EReportControlId.FILTER]?.value;
    const orderControlDataToCheck = isAddFilters
      ? {
          ...orderControlData,
          [EReportControlId.FILTER]: {
            value: [
              ...(Array.isArray(existingFilterValue) ? existingFilterValue : []),
              ...(Array.isArray(newFilterValue) ? newFilterValue : []),
            ],
          },
        }
      : orderControlData || {};

    const isChangeFilter = checkIsChangeFilter(data);
    const isHasControlFilters = checkHasControlFilters(orderControlDataToCheck);

    setIsChangingFilter(isChangeFilter || isHasControlFilters);
  }
};
